// frontend/src/components/AuthRequiredPrompt.tsx
//
// Displays a prompt when user needs to sign in to use a mini-app
// This is shown when the app uses AI, KV, File Storage, or Vector Storage APIs

import { Link } from 'react-router-dom';
import { LogIn, UserPlus, ArrowRight, Shield } from 'lucide-react';

interface AuthRequiredPromptProps {
  appName?: string;
  requiresAuth?: boolean;
}

export function AuthRequiredPrompt({ appName, requiresAuth }: AuthRequiredPromptProps) {
  const returnUrl = window.location.pathname;

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-6">
        <Shield className="h-8 w-8 text-blue-500" />
      </div>
      
      <h2 className="font-display text-2xl font-semibold text-theme-text mb-3">
        Sign In Required
      </h2>
      
      <p className="text-theme-text-secondary max-w-md mb-2">
        {appName ? (
          <>
            <span className="font-medium text-theme-text">{appName}</span> requires you to sign in to use it.
          </>
        ) : (
          <>
            This app requires you to sign in to use it.
          </>
        )}
      </p>
      
      {requiresAuth && (
        <p className="text-sm text-theme-text-muted max-w-md mb-6">
          This app uses AI, storage, or other features that require an account.
          Creating an account is free and only takes a moment.
        </p>
      )}

      <div className="space-y-4">
        <div className="flex gap-3">
          <Link 
            to={`/login?returnTo=${encodeURIComponent(returnUrl)}`}
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <LogIn className="h-4 w-4" />
            Sign In
          </Link>
          <Link 
            to={`/register?returnTo=${encodeURIComponent(returnUrl)}`}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Create Free Account
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        
        <p className="text-xs text-theme-text-muted">
          Already have an account? Sign in to continue.
        </p>
      </div>
    </div>
  );
}